import React from 'react';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import {
  FDBodyText, FDBox, FDStack, FDTitleText,
} from '@facet-wealth/design';
import MetaField from 'Components/UI/Meta/MetaForm/Components/MetaControlField';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import PlanningVersionUpgradeResponsePicker
  from 'Components/Flyouts/HouseholdInfo/Sections/Service/HouseholdSectionServicePicker/HouseholdSectionService/PlanningVersionUpgradeResponsePicker/PlanningVersionUpgradeResponsePicker';
import { ClientFlyoutSignatureDates as SignatureDates } from 'Components/UI/Signature';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import fr from 'facet-rest';
import { ACTIVE } from 'Classes/Household';
import { useSnackbarContext } from 'Context/SnackbarContext';
import ProfileSectionPicker
  from 'Components/Flyouts/HouseholdInfo/Sections/Service/HouseholdSectionServicePicker/HouseholdSectionService/ProfileSectionPicker/ProfileSectionPicker';
import {
  EstatePlanStatusOptionsContainer,
} from 'Components/Flyouts/HouseholdInfo/Sections/Service/HouseholdSectionServicePicker/HouseholdSectionService/EstatePlanStatusOptions/EstatePlanStatusOptionsContainer';

const HouseholdSectionServiceLayout = () => {
  const { onSubmit, resource, saving } = useMetaLayoutContext();
  const { displaySuccess, displayErrorMessage } = useSnackbarContext();
  const { household } = useSimpleHousehold();
  const clients = (household.clients || []).map(client => fr.resource(client));
  const isActiveHousehold = household.status === ACTIVE;

  return (
    <FDBox>
      <FDStack spacing={2} direction={'column'}>
        <FDStack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <FDTitleText value={'CSM'} />
          <MetaField
            attr={'csManagerId'}
            onChange={async () => {
              // Don't try to update to blank CSM
              if (resource.attr('csManagerId') === '') {
                return;
              }

              try {
                await onSubmit();
                displaySuccess('Updated CSM');
              } catch (e) {
                if (e.errors) {
                  displayErrorMessage(e, e.errors);
                } else {
                  displayErrorMessage(e, 'Error updating CSM');
                }
              }
            }}
          />
        </FDStack>
        <FDStack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <FDTitleText value={'Planner'} />
          <MetaField
            attr={'leadPlannerId'}
            onChange={async () => {
              // Don't try to update to blank lead planner
              if (resource.attr('leadPlannerId') === '') {
                return;
              }

              // If the household is active, get confirmation they want to change the lead planner
              // eslint-disable-next-line no-restricted-globals, no-alert
              const confirmed = confirm(
                'Are you sure you want to change Lead Planners?',
              );

              if (!confirmed) {
                return;
              }

              try {
                const resp = await onSubmit();
                if (!resp) {
                  throw new Error();
                }
                displaySuccess('Updated lead planner');
              } catch (e) {
                if (e.errors) {
                  displayErrorMessage(e, e.errors);
                } else {
                  displayErrorMessage(e, 'Error updating lead planner');
                }
              }
            }}
          />
        </FDStack>
        <PlanningVersionUpgradeResponsePicker resource={resource} />
        <FDStack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <FDTitleText value={'Requested After Hours Meetings'} />
          <MetaField
            attr={'requestedAfterHoursMeetings'}
            onChange={async () => {
              try {
                await onSubmit();
                displaySuccess('Updated Requested After Hours Meetings');
              } catch (e) {
                if (e.errors) {
                  displayErrorMessage(e, e.errors);
                } else {
                  displayErrorMessage(e, 'Error updating Requested After Hours Meetings');
                }
              }
            }}
          />
        </FDStack>
        <MetaField attr={'planningVersion'} />
        <MetaField attr={'serviceLevelCategory'} />
        <MetaField attr={'currentSubscriptionPeriod'} />
        <MetaField attr={'currentEstatePlan'} />
        <MetaField attr={'currentEstatePlanStatus'} />
        {resource.withRelationship(fr.relationships.estatePlanningStatus.name,
          (rel) => <EstatePlanStatusOptionsContainer estatePlanningStatusRelationship={rel} />,
          () => null)}
        <MetaField attr={'currentTaxFiling'} />
        <MetaField attr={'moneyBackGuarantee'} />
        <MetaField attr={'planningMeetingsInCurrentSubscription'} />
        {!isActiveHousehold && (
        <FDBox>
          <FDTitleText value={resource.attr('clientSectionTitle')} />
          <FDBodyText value={'Make a mistake? Want to update services provided? Change billing method?'} size={'sm'} />
        </FDBox>
        )}
      </FDStack>
      {isActiveHousehold && (
        <FDBox>
          <FDTitleText value={'Planning Agreement'} />
          <SignatureDates clients={clients} />
        </FDBox>
      )}
      {saving && (
        <Typography variant={'h4'} color={'primaryGray'} className="section-row">
          {'Saving...'}
        </Typography>
      )}
      <ProfileSectionPicker resource={resource} />
      <FDStack paddingY={2} direction={'column'}>
        <FDTitleText value={'User level information'} />
        <MetaField attr={'userIds'} />
        {resource.relationship(fr.relationships.openReplays.name) ? (
          <a target={'_blank'} href={resource.relationship(fr.relationships.openReplays.name).link('related').url()}>Open Replay sessions</a>
        ) : null}
      </FDStack>
    </FDBox>
  );
};

HouseholdSectionServiceLayout.propTypes = {
};

export default HouseholdSectionServiceLayout;
